import { Dialog, Heading, Text } from '@soteria/design-system-ve';

import * as styles from './PromptsMoreInfoDialog.css';

interface PromptsMoreInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  showUsernameNote?: boolean;
}

/**
 * PromptsMoreInfoDialog Component
 *
 * Provides detailed information about safety prompts, including
 * what they are, when they're regenerated automatically, and when manual
 * generation is needed.
 */
export function PromptsMoreInfoDialog({ isOpen, onClose, showUsernameNote }: PromptsMoreInfoDialogProps) {
  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title="About Safety Prompts" onClose={onClose}>
      <div className={styles.contentContainer}>
        <div>
          <Heading className={styles.sectionHeading} level="h3">
            What are Safety Prompts?
          </Heading>
          <Text>
            Safety prompts are AI-generated instructions that guide the classification service in detecting harmful content. Each prompt consists of harm
            definitions for applicable labels (hate speech, harassment, violence, etc.) tailored to your community's specific context using Azure OpenAI.
          </Text>
        </div>

        <div>
          <Heading className={styles.sectionHeading} level="h3">
            Production vs Inactive Versions
          </Heading>
          <Text>
            Each community maintains multiple prompt versions. The "Production" version is actively used by the classification service to evaluate all content
            in your community. "Inactive" versions are available for testing and comparison. Promoting a version to production means designating it as the
            active version that will be used for all classification operations.
          </Text>
        </div>

        <div>
          <Heading className={styles.sectionHeading} level="h3">
            Automatic Regeneration
          </Heading>
          <Text>
            Prompts are automatically regenerated when standard label definitions change (policy files are updated), when community metadata changes
            (description, PG rating, genres, or guidelines URL are modified), or when false positives or false negatives are reported. If nothing has changed
            and no feedback is available, generation is skipped.
          </Text>
          {showUsernameNote && <Text>Note: Username prompts are not autogenerated by community metadata changes or feedback reports.</Text>}
        </div>

        <div>
          <Heading className={styles.sectionHeading} level="h3">
            Manual Generation
          </Heading>
          <Text>
            You should manually generate prompts for new community setup, to force regeneration after updating system prompt templates, to create new versions
            for A/B testing before promoting to production, or after making significant changes to community guidelines when you want to ensure prompts reflect
            the latest context.
          </Text>
        </div>

        {showUsernameNote && (
          <div className={styles.sectionSeparator}>
            <Heading className={styles.sectionHeading} level="h3">
              Username Prompts
            </Heading>
            <Text>
              Username prompts are instructions that guide the username classification service in detecting inappropriate or harmful usernames. These prompts
              are not community-specific and apply uniformly across all communities. Each community has its own production version that can be managed
              independently.
            </Text>
          </div>
        )}
      </div>
    </Dialog>
  );
}
